/**
 * LocationsImport.js — Standortdaten Import
 * v1.0 | 2026-03-05
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdsDecisions from './AdsDecisions';
import './imports-shared.css';
import './LocationsImport.css';

var API = '/api/ads_locations_import.php';

var DE_STATES = ['Baden-Württemberg','Bayern','Berlin','Brandenburg','Bremen',
    'Hamburg','Hessen','Mecklenburg-Vorpommern','Niedersachsen',
    'Nordrhein-Westfalen','Rheinland-Pfalz','Saarland','Sachsen',
    'Sachsen-Anhalt','Schleswig-Holstein','Thüringen'];

var AT_STATES = ['Burgenland','Kärnten','Niederösterreich','Oberösterreich',
    'Salzburg','Steiermark','Tirol','Vorarlberg','Wien'];

export default function LocationsImport({ campaignId, campaignName, selectedIds, campaignList, von1, bis1 }) {
    var [data, setData]           = useState([]);
    var [loading, setLoading]     = useState(false);
    var [showForm, setShowForm]   = useState(false);
    var [importing, setImporting] = useState(false);
    var [preview, setPreview]     = useState(null);
    var [importMsg, setImportMsg] = useState(null);
    var [datumVon, setDatumVon]   = useState(von1 || '');
    var [datumBis, setDatumBis]   = useState(bis1 || '');
    var [rawText, setRawText]     = useState('');
    var [filterLand, setFilterLand] = useState('alle');

    var token = localStorage.getItem('token') || '';

    var load = useCallback(function() {
        if (!campaignId) return;
        setLoading(true);
        fetch(API + '?campaign_id=' + campaignId + '&limit=500', {
            headers: { Authorization: 'Bearer ' + token }
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) setData(json.data || []);
            setLoading(false);
        })
        .catch(function() { setLoading(false); });
    }, [campaignId, token]);

    useEffect(function() { load(); }, [load]);
    useEffect(function() { setDatumVon(von1 || ''); setDatumBis(bis1 || ''); }, [von1, bis1]);

    function handlePreview() {
        if (!rawText || !datumVon || !datumBis) return;
        setImporting(true); setImportMsg(null); setPreview(null);
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({ preview: 1, raw: rawText, datum_von: datumVon, datum_bis: datumBis, campaign_id: campaignId, campaign_name: campaignName }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) setPreview(json.rows || []);
            else setImportMsg({ ok: false, text: json.message });
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    function handleSave() {
        if (!preview) return;
        setImporting(true);
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({ save: 1, raw: rawText, datum_von: datumVon, datum_bis: datumBis, campaign_id: campaignId, campaign_name: campaignName }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) {
                setImportMsg({ ok: true, text: json.imported + ' Einträge erfasst' });
                setPreview(null); setRawText(''); setShowForm(false); load();
            } else { setImportMsg({ ok: false, text: json.message }); }
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    var periods = Array.from(new Set(data.map(function(d) { return d.datum_von + '__' + d.datum_bis; })));

    function filterRows(rows) {
        if (filterLand === 'DE') return rows.filter(function(r) { return DE_STATES.indexOf(r.standort) >= 0; });
        if (filterLand === 'AT') return rows.filter(function(r) { return AT_STATES.indexOf(r.standort) >= 0; });
        return rows;
    }

    function renderTable(rows) {
        var filtered = filterRows(rows).sort(function(a, b) {
            return (parseInt(b.impressionen) || 0) - (parseInt(a.impressionen) || 0);
        });
        if (filtered.length === 0) return <p className="imp-empty">Keine Einträge.</p>;

        var maxImpr = Math.max.apply(null, filtered.map(function(r) { return parseInt(r.impressionen) || 0; }));

        return (
            <div className="imp-table-wrap">
                <table className="imp-table">
                    <thead>
                        <tr>
                            <th>Bundesland</th>
                            <th>Land</th>
                            <th>Impr.</th>
                            <th>Anteil</th>
                            <th>Klicks</th>
                            <th>CTR %</th>
                            <th>Kosten</th>
                            <th>CPC</th>
                            <th>Conv.</th>
                            <th>Oben %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(function(r, i) {
                            var impr    = parseInt(r.impressionen) || 0;
                            var pct     = maxImpr > 0 ? Math.round((impr / maxImpr) * 100) : 0;
                            var hasConv = (parseFloat(r.conversions) || 0) > 0;
                            var hasClick = (parseInt(r.klicks) || 0) > 0;
                            return (
                                <tr key={i} className={'imp-row' + (hasConv ? ' loc-conv' : hasClick ? ' loc-click' : '')}>
                                    <td className="imp-name">{r.standort}</td>
                                    <td className="imp-land">{r.land}</td>
                                    <td className="imp-num">{impr.toLocaleString('de-DE')}</td>
                                    <td className="imp-bar-cell">
                                        <div className="imp-bar-wrap">
                                            <div className="imp-bar" style={{ width: pct + '%' }}></div>
                                            <span className="imp-bar-pct">{pct}%</span>
                                        </div>
                                    </td>
                                    <td className="imp-num">{r.klicks || 0}</td>
                                    <td className="imp-num">{r.ctr != null ? r.ctr + '%' : '—'}</td>
                                    <td className="imp-num">{r.kosten != null ? r.kosten + ' €' : '—'}</td>
                                    <td className="imp-num">{r.cpc != null ? r.cpc + ' €' : '—'}</td>
                                    <td className="imp-num">{r.conversions || 0}</td>
                                    <td className="imp-num">{r.impr_oben_pct != null ? r.impr_oben_pct + '%' : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="imp-wrap">
            <div className="imp-header">
                <span className="imp-title">📍 Standorte</span>
                <div className="imp-header-actions">
                    <select className="imp-filter-select" value={filterLand} onChange={function(e) { setFilterLand(e.target.value); }}>
                        <option value="alle">DE + AT</option>
                        <option value="DE">Deutschland</option>
                        <option value="AT">Österreich</option>
                    </select>
                    <button className="imp-btn imp-btn-import" onClick={function() { setShowForm(function(s) { return !s; }); setPreview(null); setImportMsg(null); }}>
                        {showForm ? '✕ Abbrechen' : '+ Importieren'}
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="imp-form">
                    <p className="imp-hint">Google Ads → Standorte → Zeitraum wählen → alle Zeilen kopieren → hier einfügen.</p>
                    <div className="imp-form-row">
                        <div className="imp-field">
                            <label>Von</label>
                            <input type="date" value={datumVon} onChange={function(e) { setDatumVon(e.target.value); }} />
                        </div>
                        <div className="imp-field">
                            <label>Bis</label>
                            <input type="date" value={datumBis} onChange={function(e) { setDatumBis(e.target.value); }} />
                        </div>
                    </div>
                    <div className="imp-field">
                        <label>Daten einfügen (Tab-getrennt aus Google Ads)</label>
                        <textarea rows={8} placeholder="Hier einfügen…" value={rawText} onChange={function(e) { setRawText(e.target.value); }} />
                    </div>
                    <div className="imp-form-actions">
                        <button className="imp-btn imp-btn-preview" onClick={handlePreview} disabled={importing || !rawText}>
                            {importing && !preview ? 'Prüfe…' : '🔍 Vorschau'}
                        </button>
                        {preview && (
                            <button className="imp-btn imp-btn-save" onClick={handleSave} disabled={importing}>
                                {importing ? 'Speichere…' : '💾 Erfassen (' + preview.length + ' Einträge)'}
                            </button>
                        )}
                        {importMsg && (
                            <span className={importMsg.ok ? 'imp-msg-ok' : 'imp-msg-err'}>
                                {importMsg.ok ? '✅ ' : '❌ '}{importMsg.text}
                            </span>
                        )}
                    </div>
                    {preview && preview.length > 0 && (
                        <div className="imp-preview-wrap">
                            <div className="imp-preview-title">Vorschau — {preview.length} Einträge</div>
                            {renderTable(preview)}
                        </div>
                    )}
                </div>
            )}

            {loading && <p className="imp-loading">Lädt…</p>}
            {!loading && data.length === 0 && (
                <p className="imp-empty">Noch keine Standortdaten. "+ Importieren" um zu starten.</p>
            )}

            {!loading && periods.map(function(period) {
                var parts = period.split('__');
                var dVon  = parts[0];
                var dBis  = parts[1];
                var rows  = data.filter(function(d) { return d.datum_von === dVon && d.datum_bis === dBis; });
                var totalImpr = rows.reduce(function(s, r) { return s + (parseInt(r.impressionen) || 0); }, 0);

                return (
                    <div key={period} className="imp-period">
                        <div className="imp-period-header">
                            <span>📅 {dVon} – {dBis}</span>
                            <span className="imp-period-stat">{rows.length} Standorte</span>
                            <span className="imp-period-stat">{totalImpr.toLocaleString('de-DE')} Impr. gesamt</span>
                        </div>
                        {renderTable(rows)}
                    </div>
                );
            })}

            {data.length > 0 && (
                <AdsDecisions bereich="standorte" selectedIds={selectedIds} campaignList={campaignList} />
            )}
        </div>
    );
}